import React, { Component } from 'react'
import { Row, Col, Tabs, Divider, MenuProps } from 'antd'
import { observable, makeObservable, computed, autorun, IReactionDisposer } from 'mobx'
import { observer } from 'mobx-react'
import { FeedPage } from 'src/components/Page/Page'
import { ContainerDiv, FjDropdown, FjText, PageTitle, pageTitleFontSize } from 'src/components/Common'
import { Colors } from 'src/constants/colors'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { Paths } from 'src/constants/navigation'
import { sharedDataStore } from 'src/store/DataStore'
import { RouteComponentProps } from 'react-router-dom'
import { ContentTypeIcon, getContentCategoryTitle, getContentTabs, LearningContentType } from 'src/utils/content'
import { LibraryGrid } from 'src/components/Library/LibraryGrid'
import { getQueryParam, getQueryParams } from 'src/utils/urlParams'
import { PageRenderer } from 'src/pages/PageView'
import { Page } from 'src/models/Page'
import { Layout, MoreHorizontal } from 'react-feather'
import { UserProgress } from 'src/components/Feed/UserProgress'
import { Tab } from 'rc-tabs/lib/interface'
import { ViewState, ViewWrapper } from 'src/components/Common/ViewWrapper'
import { Config } from 'src/models/Config'

export type LibraryCategory =
  | 'top'
  | 'managerPicks'
  | 'favourites'
  | LearningContentType
  | 'hub'
  | 'page'
  | 'fullsearch'
  | 'aisearch'

@observer
export class Library extends Component<RouteComponentProps> {
  @observable selectedTab: 'overview' | LibraryCategory = 'asset'
  @observable viewState: ViewState = 'initialLoad'
  @observable homepage?: Page
  disposer: IReactionDisposer

  constructor(props: RouteComponentProps) {
    super(props)
    makeObservable(this)
  }

  @computed get extraTabs() {
    const { user } = sharedDataStore
    if (user.isFaasPublic() || user.isAnonymous()) return []
    if (user.isFaasPartner()) return ['playlist', 'feedpost', 'prompt']
    return [
      'playlist',
      'feedpost',
      user.company.hasIntegratedCrmOrganization() ? 'opportunity' : undefined,
      user.company.integratedCallOrganization ? 'call' : undefined,
      'prompt',
      'managerPicks',
    ]
      .filter(Boolean)
      .filter((tab) => tab !== user.company.defaultContentTab) as LibraryCategory[]
  }

  @computed get extraTabsContextMenuOptions(): MenuProps {
    return {
      items: this.extraTabs.map((category: LibraryCategory) => ({
        key: category,
        label: getContentCategoryTitle(category),
        onClick: () => this.tabChanged(category),
        icon: (
          <ContentTypeIcon
            contentType={category}
            color={category === 'opportunity' ? Colors.cornflowerBlue : undefined}
            style={{ marginRight: '8px' }}
          />
        ),
      })),
    }
  }

  @computed get tabs(): Tab[] {
    return [
      ...this.categories.map((category) => ({
        key: category,
        label: category === 'overview' ? 'Overview' : getContentCategoryTitle(category),
      })),
      this.selectedTab !== 'overview' && this.extraTabs.includes(this.selectedTab)
        ? {
            key: this.selectedTab,
            label: getContentCategoryTitle(this.selectedTab),
          }
        : undefined,
      this.extraTabs.length
        ? {
            key: 'contextMenu',
            label: (
              <ContainerDiv marginBottom="-5px">
                <FjDropdown menu={this.extraTabsContextMenuOptions}>
                  <MoreHorizontal size={20} color={Colors.cottonSeed} />
                </FjDropdown>
              </ContainerDiv>
            ),
          }
        : undefined,
    ]
  }

  @computed get categories() {
    const { user, config } = sharedDataStore
    if (user.isAnonymous() || user.isFaasPublic()) return ['course', 'learningpath'] as LibraryCategory[]
    const defaultCategories = [
      user.hubsCount > 0 ? 'hub' : undefined,
      'asset',
      'dealroom',
      'course',
      'learningpath',
    ].filter(Boolean) as LibraryCategory[]

    const { libraryOverviewPageId } = config
    const overviewTab = (libraryOverviewPageId && !(user.isFaasPublic() || user.isAnonymous()) ? ['overview'] : []) as (
      | 'overview'
      | LibraryCategory
    )[]
    return [...overviewTab, ...getContentTabs(defaultCategories)]
  }

  async componentDidMount() {
    await Config.getConfigs()
    this.disposer = autorun(() => {
      if (sharedDataStore.config.libraryOverviewPageId) {
        this.getLibraryHomepage()
      }
    })
    this.setCategory()
  }

  componentDidUpdate(prevProps: RouteComponentProps) {
    if (this.props.location.search !== prevProps.location.search) {
      this.setCategory()
    }
  }

  componentWillUnmount(): void {
    this.disposer?.()
  }

  getLibraryHomepage = async () => {
    const { libraryOverviewPageId } = sharedDataStore.config
    if (
      !libraryOverviewPageId ||
      this.homepage ||
      sharedDataStore.user.isFaasPublic() ||
      sharedDataStore.user.isAnonymous()
    )
      return
    try {
      this.viewState = 'loading'
      this.homepage = await Page.get(libraryOverviewPageId)
      this.viewState = 'idle'
    } catch (err) {
      if (err.response?.status === 404) this.viewState = 'notFound'
      else {
        this.viewState = 'error'
        sharedAppStateStore.handleError(err)
      }
    }
  }

  setCategory = () => {
    const contentType = getQueryParam('contentType') as LibraryCategory
    this.selectedTab = [...this.extraTabs, ...this.categories].includes(contentType) ? contentType : this.categories[0]
    if (this.selectedTab === 'overview') sharedAppStateStore.navigate(Paths.getLibraryPath(), true)
    else sharedAppStateStore.navigate(Paths.getLibraryPath(this.selectedTab, getQueryParams()), true)
  }

  handleNavigation = (params?: any) => {
    if (this.selectedTab === 'overview') return
    sharedAppStateStore.navigate(Paths.getLibraryPath(this.selectedTab, params))
  }

  tabChanged = (key: 'contextMenu' | 'overview' | LibraryCategory) => {
    if (key === 'contextMenu') return
    if (key === 'overview') sharedAppStateStore.navigate(Paths.getLibraryPath())
    else sharedAppStateStore.navigate(Paths.getLibraryPath(key, { sort_by: getQueryParam('sort_by') ?? 'recent' }))
  }

  render() {
    const isFaasPublicOrAnonymous = sharedDataStore.user.isFaasPublic() || sharedDataStore.user.isAnonymous()
    const { externalAcademy } = sharedAppStateStore
    return (
      <FeedPage title="Library">
        {sharedDataStore.user.isFaasPublic() ? (
          <>
            <PageTitle textAlign="left" color={Colors.midnightBlue} marginBottom="20px" fontSize="28px">
              Hello {sharedDataStore.user.getFirstName()}!
            </PageTitle>
            <Divider style={{ color: Colors.sharkOpacity10 }} />

            <ContainerDiv marginBottom="30px">
              <UserProgress
                user={sharedDataStore.user}
                display="horizontal"
                contentTypeChartOptions={['course', 'learningpath']}
                emptyContentProps={{
                  title: "Looks like you haven't started anything yet...",
                  imgMaxWidth: '0px',
                  paddingVertical: '0px',
                  titleMarginTop: '0px',
                }}
              />
            </ContainerDiv>
            <Divider style={{ color: Colors.sharkOpacity10 }} />
          </>
        ) : null}

        <ContainerDiv marginBottom={isFaasPublicOrAnonymous ? '15px' : '30px'} textAlign="left">
          <Row align="middle">
            <Col xs={24} md={6} lg={5} xl={4}>
              <ContainerDiv textAlign="left" display="flex" alignItems="center" gap={8}>
                {!isFaasPublicOrAnonymous ? (
                  <Layout size={sharedAppStateStore.isMobile ? 24 : 40} color={Colors.chambray} />
                ) : null}
                <FjText
                  color={sharedDataStore.user.isFaasPublic() ? Colors.midnightBlue : Colors.chambray}
                  fontSize={sharedDataStore.user.isFaasPublic() ? '24px' : pageTitleFontSize}
                  fontWeight="semi-bold"
                >
                  {sharedDataStore.user.isFaasPublic()
                    ? `All ${getContentCategoryTitle(this.selectedTab as LibraryCategory)}`
                    : 'Library'}
                </FjText>
              </ContainerDiv>
            </Col>

            {/* {!sharedDataStore.user.isFaasPublicOrPartner() ? (
              <Col
                xs={24}
                md={18}
                lg={19}
                xl={20}
                style={{ marginTop: sharedAppStateStore.isMobile ? '20px' : undefined }}
              >
                <TrendingTags />
              </Col>
            ) : null} */}
          </Row>
        </ContainerDiv>

        <ContainerDiv>
          <Tabs key={this.selectedTab} activeKey={this.selectedTab} onChange={this.tabChanged} items={this.tabs} />
          {this.selectedTab === 'overview' ? (
            <ViewWrapper viewState={this.viewState}>
              {this.viewState === 'idle' ? <PageRenderer page={this.homepage} trackingSource="library" /> : null}
            </ViewWrapper>
          ) : (
            <LibraryGrid
              showSort
              showFilter={
                isFaasPublicOrAnonymous ? !!sharedAppStateStore.externalAcademy?.data.publicFilters?.length : true
              }
              apiQueryParams={
                isFaasPublicOrAnonymous ? { company_id: sharedAppStateStore.externalAcademy?.companyId } : undefined
              }
              handleNavigation={this.handleNavigation}
              overrideFilters={
                isFaasPublicOrAnonymous && externalAcademy?.getOverrideFilters()
                  ? externalAcademy?.getOverrideFilters()
                  : undefined
              }
              {...this.props}
            />
          )}
        </ContainerDiv>
      </FeedPage>
    )
  }
}
